/**
 * Auto-Responder Search and Filtering Component
 *
 * Provides advanced search and filtering capabilities for auto-responders
 * with real-time filtering, tag selection, and status filtering.
 */

import { useState, useEffect, useCallback, useRef } from "react";

// Custom debounce hook
function useDebounce<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const timeoutRef = useRef<number | undefined>(undefined);

  return useCallback(
    ((...args: Parameters<T>) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => callback(...args), delay);
    }) as T,
    [callback, delay]
  );
}

export interface AutoResponderFilters {
  search?: string;
  isActive?: boolean;
  tags?: string[];
  conversationId?: string;
}

interface AutoResponderSearchFiltersProps {
  filters: AutoResponderFilters;
  onFiltersChange: (filters: AutoResponderFilters) => void;
  availableTags?: string[];
  loading?: boolean;
  className?: string;
}

export function AutoResponderSearchFilters({
  filters,
  onFiltersChange,
  availableTags = [],
  loading = false,
  className = "",
}: AutoResponderSearchFiltersProps) {
  const [localSearch, setLocalSearch] = useState(filters.search || "");
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

  // Debounced search to avoid too many API calls
  const debouncedSearchChange = useDebounce((searchValue: string) => {
    onFiltersChange({
      ...filters,
      search: searchValue.trim() || undefined,
    });
  }, 300);

  useEffect(() => {
    debouncedSearchChange(localSearch);
  }, [localSearch, debouncedSearchChange]);

  const handleSearchChange = (value: string) => {
    setLocalSearch(value);
  };

  const handleStatusFilterChange = (status: "all" | "active" | "inactive") => {
    const isActive = status === "all" ? undefined : status === "active";
    onFiltersChange({
      ...filters,
      isActive,
    });
  };

  const handleTagToggle = (tag: string) => {
    const currentTags = filters.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter((t) => t !== tag)
      : [...currentTags, tag];

    onFiltersChange({
      ...filters,
      tags: newTags.length > 0 ? newTags : undefined,
    });
  };

  const clearAllFilters = () => {
    setLocalSearch("");
    onFiltersChange({});
  };

  const hasActiveFilters = !!(
    filters.search ||
    filters.isActive !== undefined ||
    (filters.tags && filters.tags.length > 0) ||
    filters.conversationId
  );

  const getStatusFilterValue = () => {
    if (filters.isActive === undefined) return "all";
    return filters.isActive ? "active" : "inactive";
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <svg
            className="h-4 w-4 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          type="text"
          placeholder="Search auto-responders by name or content..."
          value={localSearch}
          onChange={(e) => handleSearchChange(e.target.value)}
          className="block w-full pl-10 pr-12 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          disabled={loading}
        />
        {localSearch && (
          <button
            onClick={() => handleSearchChange("")}
            className="absolute inset-y-0 right-0 pr-3 flex items-center"
            disabled={loading}
          >
            <svg
              className="h-4 w-4 text-gray-400 hover:text-gray-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        )}
      </div>

      {/* Filter Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          {/* Status Filter */}
          <div className="flex items-center space-x-2">
            <label className="text-sm font-medium text-gray-700">Status:</label>
            <select
              value={getStatusFilterValue()}
              onChange={(e) =>
                handleStatusFilterChange(
                  e.target.value as "all" | "active" | "inactive"
                )
              }
              className="text-sm border border-gray-300 rounded-md px-2 py-1 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            >
              <option value="all">All</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {/* Advanced Filters Toggle */}
          <button
            onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center space-x-1"
            disabled={loading}
          >
            <svg
              className={`h-4 w-4 transition-transform ${
                showAdvancedFilters ? "rotate-180" : ""
              }`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
            <span>Advanced Filters</span>
          </button>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="text-sm text-gray-600 hover:text-gray-700 font-medium flex items-center space-x-1"
            disabled={loading}
          >
            <svg
              className="h-4 w-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            <span>Clear All</span>
          </button>
        )}
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 space-y-4">
          {/* Tag Filters */}
          {availableTags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter by Tags:
              </label>
              <div className="flex flex-wrap gap-2">
                {availableTags.map((tag) => {
                  const isSelected = filters.tags?.includes(tag) || false;
                  return (
                    <button
                      key={tag}
                      onClick={() => handleTagToggle(tag)}
                      className={`px-3 py-1 text-sm rounded-full border transition-colors ${
                        isSelected
                          ? "bg-blue-100 border-blue-300 text-blue-800"
                          : "bg-white border-gray-300 text-gray-700 hover:bg-gray-50"
                      }`}
                      disabled={loading}
                    >
                      {tag}
                      {isSelected && (
                        <span className="ml-1 text-blue-600">×</span>
                      )}
                    </button>
                  );
                })}
              </div>
              {filters.tags && filters.tags.length > 0 && (
                <p className="text-xs text-gray-500 mt-2">
                  Showing auto-responders with any of the selected tags
                </p>
              )}
            </div>
          )}

          {/* Conversation Filter */}
          {filters.conversationId && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filtered by Conversation:
              </label>
              <div className="flex items-center space-x-2">
                <span className="px-3 py-1 text-sm bg-purple-100 border border-purple-300 text-purple-800 rounded-full">
                  Conversation: {filters.conversationId}
                </span>
                <button
                  onClick={() =>
                    onFiltersChange({ ...filters, conversationId: undefined })
                  }
                  className="text-sm text-red-600 hover:text-red-700"
                  disabled={loading}
                >
                  Remove
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="flex items-center space-x-2 text-sm text-gray-600">
          <span>Active filters:</span>
          <div className="flex flex-wrap gap-1">
            {filters.search && (
              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs">
                Search: "{filters.search}"
              </span>
            )}
            {filters.isActive !== undefined && (
              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                Status: {filters.isActive ? "Active" : "Inactive"}
              </span>
            )}
            {filters.tags && filters.tags.length > 0 && (
              <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs">
                Tags: {filters.tags.length}
              </span>
            )}
            {filters.conversationId && (
              <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded-full text-xs">
                Conversation Filter
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
